import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { cartItemsArrayPropTypes } from 'helpers/propTypes';
import { getLocalStorage } from 'helpers/storage';

import Breadcrumb from 'components/commons/Breadcrumb';
import DescriptionItem from 'components/commons/DescriptionItem';
import QuantitySelector from 'components/commons/QuantitySelector';
import Button from 'components/commons/Button';
import Benefit from 'components/commons/Benefit';
import Loading from 'components/commons/Loading';

import './Cart.scss';

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const Cart = (props) => {
  const {
    cartSize,
    items,
    minimumPurchase,
    onQuantityChange,
    onRemoveFromCart,
    subtotal,
    priceDiscount,
    onItemClick,
    loading,
    cuotasMP,
    onOpenPaymentModal,
    showMPButton,
    tieneEnvios,
  } = props;
  const showCart = cartSize > 0;
  const total = priceDiscount != null ? priceDiscount : subtotal;

  const renderEmptyCart = () => (
    <section className="cart-empty-state" data-testid="empty-cart">
      <div className="cart-empty-state__icon">
        <i className="fa fa-shopping-cart" aria-hidden="true" />
      </div>
      <h2>El carrito está vacío</h2>
      <p>Agregá productos para preparar tu pedido.</p>
      <Button text="Ver productos" buttonClass="cart-empty-state__button" href="/" />
    </section>
  );

  const findProductWholesale = (id, item) => {
    const product = item.product_wholesaler.find((prod) => prod.id === id);
    return product ? product.price * product.quantity : item.price;
  };

  const findIDProductWholesale = (subItem) => {
    const values = Object.values(subItem);
    const firstArray = values[0];
    const firstObject = firstArray[0];
    const { id } = firstObject.wholesale;
    if (id > 0) return `Precio por ${firstObject.wholesale.quantity}`;
    return 'Precio';
  };

  const findPriceProductWholesale = (subItem, item) => {
    const values = Object.values(subItem);
    const firstArray = values[0];
    const firstObject = firstArray[0];
    const { id } = firstObject.wholesale;
    if (id > 0) {
      const product = item.product_wholesaler.find((prod) => prod.id === id);
      return product ? product.price * product.quantity : item.price;
    }
    return item.sale ? item.sale : item.price;
  };

  const renderDescriptions = (descriptions, colors, item, descriptionId) => {
    const color = colors.find(
      ({ stock_description_id }) => stock_description_id === Number(descriptionId),
    );
    const combinations = descriptions;

    return !!combinations.length && (
      <div className="cart-item-description-option" key={`item-${item.id}-description-${descriptionId}`}>
        {combinations.map((combination, index) => (
          <Fragment key={`${item.id}-${combination.description.id}-${combination.wholesale.id}`}>
            {index === 0 && (
              <DescriptionItem
                description={color}
                onQuantityChange={(value, desc) => {
                  onQuantityChange(item, value, desc, combination.wholesale);
                }}
                showQuantitySelector={combination.description.id === color.id}
                storage={{ quantity: combination.quantity }}
              />
            )}
            {combination.description.id !== color.id && (
              <DescriptionItem
                description={combination.description}
                onQuantityChange={(value, desc) => {
                  onQuantityChange(item, value, desc, combination.wholesale);
                }}
                storage={{ quantity: combination.quantity }}
                othersWholesale={combination.othersSubItemsWholesale}
              />
            )}
          </Fragment>
        ))}
      </div>
    );
  };

  const renderPrice = (subItemDescription) => {
    for (const key in subItemDescription) {
      if (Object.prototype.hasOwnProperty.call(subItemDescription, key)) {
        const cartItems = subItemDescription[key];
        for (const cartItem of cartItems) {
          if (cartItem.wholesale && cartItem.wholesale.bool) return true;
        }
      }
    }
    return false;
  };

  const renderPriceDescriptions = (talles, price) => {
    const uniqueArray = talles.reduce((accumulator, currentObj) => {
      const existingObj = accumulator.find(
        (obj) => obj.description.stock_description_id === currentObj.description.stock_description_id,
      );
      if (!existingObj) accumulator.push(currentObj);
      return accumulator;
    }, []);

    return (
      <div className="precios-talles">
        {uniqueArray.map((cartItem, index) => {
          const stockDescription = cartItem.description.stock_descriptions;
          return (
            <React.Fragment key={stockDescription?.id || index}>
              <span>
                {stockDescription?.name}: {formatMoney(
                  cartItem.description.price && cartItem.description.price > 0
                    ? cartItem.description.price
                    : price,
                )}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderRemoveButton = (item) => (
    <button
      type="button"
      className="cart-item-remove"
      aria-label={`Quitar ${item.name}`}
      data-testid="button"
      onClick={() => onRemoveFromCart(item)}
    >
      <i className="fa fa-trash-o" aria-hidden="true" />
      <span>Quitar</span>
    </button>
  );

  const renderItemMedia = (item) => (
    <button
      type="button"
      className="cart-item-media"
      onClick={() => onItemClick(item)}
      aria-label={`Ver ${item.name}`}
    >
      <img src={item.image1} alt={item.name} />
    </button>
  );

  const renderSimplePurchaseBox = (item, quantity) => {
    const unitPrice = Number(item.sale ? item.sale : item.price);
    const itemSubtotal = unitPrice * quantity;

    return (
      <div className="cart-purchase-grid">
        <div className="cart-price-card">
          <span>Precio unitario</span>
          <strong className="price-text">{formatMoney(unitPrice)}</strong>
          {!!item.sale && <del>{formatMoney(item.price)}</del>}
        </div>
        <div className="cart-quantity-card">
          <span>Cantidad</span>
          <QuantitySelector
            limit={item.stock}
            onQuantityChange={(value) =>
              onQuantityChange(item, value, null, {
                id: 0,
                bool: false,
                quantity: 0,
              })}
            quantity={quantity}
          />
        </div>
        <div className="cart-price-card cart-price-card--subtotal">
          <span>Subtotal</span>
          <strong className="price-text">{formatMoney(itemSubtotal)}</strong>
        </div>
      </div>
    );
  };

  const renderCartItem = ({ item, quantity, colors, subItems, subItemsDescriptions }) => {
    if (!item) return null;
    const hasSimplePrice = !(item.product_wholesaler.length > 0) && item.stock_product_descriptions.length === 0;

    return (
      <article className="cart-item" key={`cart-item-${item.id}`}>
        {renderItemMedia(item)}
        <div className="cart-item-main">
          <div className="cart-item-heading">
            <div>
              <span className="cart-item-kicker">Producto</span>
              <h3 className="cart-item-title" onClick={() => onItemClick(item)} role="presentation">
                {item.name}
              </h3>
              <p className="cart-item-description">{item.description}</p>
            </div>
            {renderRemoveButton(item)}
          </div>

          {hasSimplePrice && renderSimplePurchaseBox(item, quantity)}

          <div className="descriptions-col">
            {colors && (
              <div className="descriptions-container">
                {Object.values(subItemsDescriptions || {}).some(
                  (value) => typeof value === 'object' && Object.keys(value).length > 0,
                ) && (
                  <div className="precios-container-wrapper">
                    <span className="cart-options-title">Opciones elegidas</span>
                    {Object.values(subItemsDescriptions || {}).map((subItemDescription, idx) => (
                      <Fragment key={`${item.id}-subdesc-${idx}`}>
                        {Object.keys(subItemDescription).length > 0 && (
                          <div className="precios-container">
                            <div className="cart-price-card cart-price-card--option">
                              <span className="title-description search-item">
                                {findIDProductWholesale(subItemDescription)}
                              </span>
                              <strong className="cart-item-price price-text">
                                {!renderPrice(subItemDescription) ? (
                                  <>
                                    Precio por talle
                                    {renderPriceDescriptions(
                                      Object.values(subItemDescription).flatMap((desc) =>
                                        Array.isArray(desc)
                                          ? desc.filter(
                                              (it) => it.description.stock_descriptions.stock_category_id === 2,
                                            )
                                          : desc.description.stock_descriptions.stock_category_id === 2
                                            ? [desc]
                                            : [],
                                      ),
                                      item.price,
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {!!item.sale && item.product_wholesaler.length === 0 && (
                                      <del>{formatMoney(item.price)}</del>
                                    )}
                                    {formatMoney(findPriceProductWholesale(subItemDescription, item))}
                                  </>
                                )}
                              </strong>
                            </div>
                            {Object.entries(subItemDescription).map(
                              ([descriptionId, descriptionArray]) =>
                                renderDescriptions(
                                  descriptionArray,
                                  colors,
                                  item,
                                  descriptionId,
                                ),
                            )}
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                )}
              </div>
            )}

            {subItems?.length > 0 && (
              <div className="precios-container-wrapper">
                <span className="cart-options-title">Precios por cantidad</span>
                {subItems.map((subItem) => (
                  <div className="precios-container" key={`${item.id}-wh-${subItem.wholesale.id}`}>
                    <div className="cart-price-card cart-price-card--option">
                      <span className="title-description search-item">
                        {subItem.wholesale.id > 0
                          ? `Precio por ${subItem.wholesale.quantity}`
                          : 'Precio'}
                      </span>
                      <strong className="cart-item-price price-text">
                        {formatMoney(
                          subItem.wholesale.id > 0
                            ? findProductWholesale(subItem.wholesale.id, item)
                            : item.price,
                        )}
                      </strong>
                    </div>
                    <div className="cart-quantity-card">
                      <span>Cantidad</span>
                      <QuantitySelector
                        limit={item.stock}
                        onQuantityChange={(value) =>
                          onQuantityChange(item, value, null, subItem.wholesale)}
                        quantity={subItem.quantity}
                        quantityOthersWholesale={subItems
                          .filter(({ wholesale }) => wholesale.id !== subItem.wholesale.id)
                          .map((otherProduct) => otherProduct.quantity)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </article>
    );
  };

  const renderFullCart = () => (
    <section className="cart cart--standard" data-testid="full-cart">
      <div className="cart-head">
        <div>
          <span className="cart-head__eyebrow">Tu pedido</span>
          <h1>{`Hay ${cartSize} producto${cartSize > 1 ? 's' : ''}`}</h1>
        </div>
        <div className="cart-head__meta">
          <span>{cartSize} ítems</span>
          <strong>{formatMoney(total)}</strong>
        </div>
      </div>

      <div className="cart-items">
        {items.map(renderCartItem)}
      </div>
    </section>
  );

  const renderCheckoutDetails = () => (
    <aside className="cart-checkout-panel" data-testid="checkout-details">
      <div className="cart-checkout-panel__heading">
        <span>Resumen</span>
        <h2>Total del pedido</h2>
      </div>

      <ul className="checkout-details">
        <li>
          <span>Subtotal</span>
          {priceDiscount === null ? (
            <strong>{formatMoney(subtotal)}</strong>
          ) : (
            <del>{formatMoney(subtotal)}</del>
          )}
        </li>
        {priceDiscount != null && (
          <li className="checkout-details__discount">
            <span>Con descuento {getLocalStorage('percentage')}%</span>
            <strong>{formatMoney(priceDiscount)}</strong>
          </li>
        )}
        {cuotasMP && cuotasMP.length > 0 && showMPButton && (
          <li className="checkout-details__link">
            <button type="button" onClick={onOpenPaymentModal}>
              Ver bancos con cuotas sin interés
            </button>
          </li>
        )}
        <li className="checkout-details__total">
          <span>Total</span>
          <strong>{formatMoney(total)}</strong>
        </li>
      </ul>

      <div className="cart-checkout-actions">
        {tieneEnvios && (
          <Button text="Envío a domicilio" buttonClass="checkout-button" href="/envio-a-domicilio" />
        )}
        <Button text="Retiro en sucursal" buttonClass="checkout-button checkout-button--secondary" href="/retiro-en-sucursal" />
      </div>

      <p className="cart-checkout-panel__note">
        Revisá cantidades y opciones antes de continuar.
      </p>
    </aside>
  );

  return (
    <div data-testid="page-cart" className="page-cart page-cart--standard">
      <div className="page-cart__content">
        <div className="container cart-container">
          <Breadcrumb urls={['/carrito']} names={['Carrito']} />
          {!loading ? (
            !showCart ? (
              renderEmptyCart()
            ) : (
              <div className="cart-layout">
                <div className="cart-layout__main">
                  {renderFullCart()}
                </div>
                <div className="cart-layout__summary">
                  {renderCheckoutDetails()}
                </div>
              </div>
            )
          ) : <Loading />}
        </div>

        <div className="section section--compact">
          <Benefit minimumPurchase={minimumPurchase} />
        </div>
      </div>
    </div>
  );
};

Cart.defaultProps = {
  priceDiscount: null,
};

Cart.propTypes = {
  cartSize: PropTypes.number.isRequired,
  items: cartItemsArrayPropTypes.isRequired,
  minimumPurchase: PropTypes.number.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  onRemoveFromCart: PropTypes.func.isRequired,
  subtotal: PropTypes.number.isRequired,
  onItemClick: PropTypes.func.isRequired,
  priceDiscount: PropTypes.number,
  loading: PropTypes.bool.isRequired,
};

export default Cart;
